import React, { useState, useMemo, useEffect } from 'react';
import { MapContainer as LeafletMap, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { RefreshIcon } from '../Icons';
import PaciMap from '../PaciMap';

// Below this zoom level, lands are grouped into per-parent-tag cluster
// circles instead of individual pins (too many pins to read at a glance).
const CLUSTER_ZOOM_THRESHOLD = 12;

// Roughly bounds the pannable area around Sabah Al-Ahmad Sea City —
// dragging past this box springs back inside it instead of letting the map
// wander off into open desert/ocean indefinitely.
//
// Centred on where the listings actually are (they span roughly
// 28.602-28.684 N, 48.321-48.386 E) with about 0.06 degrees — ~6.5km — of
// slack on every side. The earlier hand-picked corner points were centred
// ~0.05 degrees west of the real listings, which left barely a third of a
// screen of eastward room at zoom 14: panning towards the coast hit the
// wall almost immediately and read as the map being stuck. Keep this box
// centred on the listings if their spread ever changes.
const MAP_BOUNDS = L.latLngBounds(
  [28.54, 48.26],
  [28.75, 48.45]
);

// Applied imperatively (like CenterSync below) rather than as a prop on
// <LeafletMap> — same reasoning: it's simplest to just set it once, on the
// live map instance, rather than rely on react-leaflet re-reading props.
//
// The bounds are only applied while they're actually *bigger* on screen than
// the viewport. When maxBounds is smaller than the window, Leaflet has
// nowhere legal to move the center to, so it pins it and dragging becomes a
// dead no-op — and since the whole box fits on screen at the default zoom
// (11), that froze dragging on load at every zoom from minZoom up to ~13 on
// a desktop window, not just at the box's edges. Zoomed in past that, the
// box is larger than the screen and does its real job of keeping the map
// from wandering off into open desert/ocean.
const MaxBoundsLock = () => {
  const map = useMap();
  useEffect(() => {
    const syncBounds = () => {
      const view = map.getSize();
      const nw = map.latLngToContainerPoint(MAP_BOUNDS.getNorthWest());
      const se = map.latLngToContainerPoint(MAP_BOUNDS.getSouthEast());
      const fitsOnScreen = se.x - nw.x <= view.x || se.y - nw.y <= view.y;
      map.setMaxBounds(fitsOnScreen ? null : MAP_BOUNDS);
    };

    syncBounds();
    map.on('zoomend resize', syncBounds);
    return () => map.off('zoomend resize', syncBounds);
  }, [map]);
  return null;
};

// Reports the map's current zoom back up whenever it changes. Must be
// rendered as a child of <LeafletMap> since useMapEvents needs map context.
const ZoomWatcher = ({ onZoomChange }) => {
  useMapEvents({
    zoomend: (e) => onZoomChange(e.target.getZoom())
  });
  return null;
};

// <LeafletMap center={center}> only reads `center` once, at initial mount —
// react-leaflet doesn't react to prop changes after that. Without this,
// opening a specific land's deep link (which sets `center` asynchronously,
// once that land is fetched — after the map has already mounted at the
// default view) left the actual map showing the default region while the
// details panel showed the right land. This keeps the live Leaflet map in
// sync with `center` any time it changes, at whatever zoom is current.
//
// Opening a land recentres the map on it and then leaves the map alone.
// An earlier version pinned the map to the selected land while its details
// were open — dragging and keyboard pan disabled, plus a dragend/zoomend
// snap-back — to stop the property drifting off screen. That read as the
// map being broken: the details panel is open for most of the time anyone
// spends looking at a property, so in practice the map simply refused to
// drag. Panning now stays live; the recentre on open is enough.
const CenterSync = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, map.getZoom());
  }, [center, map]);

  return null;
};

// A tag nested under a parent (via nested_tags) clusters under that parent;
// a top-level tag clusters under itself.
const getEffectiveParentTag = (tag, nestedTags, tagsById) => {
  const nesting = nestedTags.find((n) => n.child_tag_id === tag.id);
  return nesting ? (tagsById[nesting.parent_tag_id] || tag) : tag;
};

const median = (nums) => {
  const sorted = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

// ~3km in this region — generous enough to cover a real, spread-out phase,
// but tight enough to reject a stray test listing with real-but-wrong
// coordinates (which happened: a handful of QA lands pulled a cluster's
// marker ~600m away from its actual lands by skewing a plain mean).
const OUTLIER_DEGREES = 0.03;

// Groups lands by their effective parent tag, computing each group's
// centroid (for marker placement) and count (for the badge).
const buildParentClusters = (lands, tags, nestedTags) => {
  const tagsById = {};
  tags.forEach((tag) => { tagsById[tag.id] = tag; });

  const clusters = {};
  lands.forEach((land) => {
    if (!land.tags || land.tags.length === 0 || !land.latitude || !land.longitude) return;
    const parentTag = getEffectiveParentTag(land.tags[0], nestedTags, tagsById);
    if (!clusters[parentTag.id]) {
      clusters[parentTag.id] = { tag: parentTag, lands: [] };
    }
    clusters[parentTag.id].lands.push(land);
  });

  return Object.values(clusters).map(({ tag, lands: clusterLands }) => {
    const lats = clusterLands.map((l) => parseFloat(l.latitude));
    const lngs = clusterLands.map((l) => parseFloat(l.longitude));
    const medianLat = median(lats);
    const medianLng = median(lngs);

    const inliers = clusterLands.filter((l) =>
      Math.abs(parseFloat(l.latitude) - medianLat) < OUTLIER_DEGREES &&
      Math.abs(parseFloat(l.longitude) - medianLng) < OUTLIER_DEGREES
    );
    const pointsForCentroid = inliers.length > 0 ? inliers : clusterLands;

    const lat = pointsForCentroid.reduce((sum, l) => sum + parseFloat(l.latitude), 0) / pointsForCentroid.length;
    const lng = pointsForCentroid.reduce((sum, l) => sum + parseFloat(l.longitude), 0) / pointsForCentroid.length;
    return { tag, count: clusterLands.length, center: [lat, lng], lands: clusterLands };
  });
};

const getClusterRadius = (cluster) => Math.min(70, 44 + cluster.count * 1.5) / 2;

// Parent-tag clusters for the same physical development often sit almost on
// top of each other (adjacent construction phases). Nudges overlapping
// circles apart in pixel space so they stay individually readable, then
// converts back to lat/lng so they still behave like normal map markers.
const declutterClusters = (clusters, map) => {
  if (!map || clusters.length < 2) return clusters;

  const points = clusters.map((c) => ({ ...c, point: map.latLngToContainerPoint(c.center) }));

  for (let iter = 0; iter < 40; iter++) {
    let moved = false;
    for (let i = 0; i < points.length; i++) {
      for (let j = i + 1; j < points.length; j++) {
        const a = points[i];
        const b = points[j];
        const minDist = (getClusterRadius(a) + getClusterRadius(b)) * 1.15;
        const dx = b.point.x - a.point.x;
        const dy = b.point.y - a.point.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 0.01;
        if (dist < minDist) {
          moved = true;
          const overlap = (minDist - dist) / 2;
          const ux = dx / dist;
          const uy = dy / dist;
          a.point.x -= ux * overlap;
          a.point.y -= uy * overlap;
          b.point.x += ux * overlap;
          b.point.y += uy * overlap;
        }
      }
    }
    if (!moved) break;
  }

  return points.map((p) => ({ ...p, center: map.containerPointToLatLng(p.point) }));
};

const getClusterIcon = (cluster) => {
  const size = Math.min(70, 44 + cluster.count * 1.5);
  return L.divIcon({
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        background-color: ${cluster.tag.color};
        border: 3px solid white;
        box-shadow: 0 2px 10px rgba(0,0,0,0.35);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
        cursor: pointer;
        text-align: center;
        line-height: 1.1;
      ">
        <span style="font-size: 15px;">${cluster.count}</span>
        <span style="font-size: 9px; max-width: ${size - 10}px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${cluster.tag.title}</span>
      </div>
    `,
    className: 'cluster-marker',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
};

const MapView = ({
  showPaciMap,
  filteredLands,
  language,
  onOpenDetail,
  center,
  getMarkerIcon,
  formatPrice,
  t,
  isMobile,
  fetchLands,
  WhatsAppButtons,
  tags = [],
  nestedTags = []
}) => {
  const [zoom, setZoom] = useState(11);
  const [mapInstance, setMapInstance] = useState(null);

  const clusters = useMemo(
    () => buildParentClusters(filteredLands, tags, nestedTags),
    [filteredLands, tags, nestedTags]
  );

  // Recomputed per zoom level (pixel distances between cluster centers only
  // change when zoom changes, not on pan — see declutterClusters).
  const declutteredClusters = useMemo(
    () => declutterClusters(clusters, mapInstance),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clusters, mapInstance, zoom]
  );

  const showClusters = zoom <= CLUSTER_ZOOM_THRESHOLD && clusters.length > 0;

  // Zoom in past CLUSTER_ZOOM_THRESHOLD so individual pins actually show —
  // a plain "one step" zoom wasn't enough from a fully zoomed-out view (it
  // could still land on another cluster level). fitBounds-ing to every
  // member land isn't used here either, since one outlier would zoom out
  // instead of in.
  const handleClusterClick = (cluster) => {
    if (!mapInstance) return;
    mapInstance.setView(cluster.center, Math.max(zoom + 1, CLUSTER_ZOOM_THRESHOLD + 2));
  };

  return (
    <div className="w-full h-full relative">
      {showPaciMap ? (
        <PaciMap lands={filteredLands} language={language} onOpenDetail={onOpenDetail} />
      ) : (
        <LeafletMap
          center={center}
          zoom={11}
          style={{ width: '100%', height: '100%', borderRadius: '12px' }}
          scrollWheelZoom={true}
          zoomControl={false}
          attributionControl={false}
          maxZoom={18}
          minZoom={8}
          maxBoundsViscosity={0.5}
          ref={setMapInstance}
        >
          <ZoomWatcher onZoomChange={setZoom} />
          <CenterSync center={center} />
          <MaxBoundsLock />
          <TileLayer
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            maxZoom={18}
          />

          {/* Parent-Tag Clusters (zoomed out) or Property Markers (zoomed in) */}
          {showClusters ? (
            declutteredClusters.map((cluster) => (
              <Marker
                key={`cluster-${cluster.tag.id}`}
                position={cluster.center}
                icon={getClusterIcon(cluster)}
                eventHandlers={{
                  click: () => handleClusterClick(cluster)
                }}
              />
            ))
          ) : (
          filteredLands
            .filter((land) => land.latitude && land.longitude)
            .map((land) => (
            <Marker
              key={land.id}
              position={[parseFloat(land.latitude), parseFloat(land.longitude)]}
              icon={getMarkerIcon(land)}
              eventHandlers={{
                click: () => onOpenDetail(land)
              }}
            >
              <Popup>
                <div className="p-2 min-w-[200px] text-center">
                  <h3 className="text-base font-semibold text-gray-900 mb-2">
                    <span className="text-xs text-gray-600 font-normal">{t('كود رقم', 'Code No')} : </span>
                    {land.code_number || land.land_number || 'Property'}
                  </h3>
                  {land.price && (
                    <p className="text-sm text-gray-700 mb-3 font-medium">
                      <span className="text-xs text-gray-600 font-normal">{t('السعر', 'Price')} : </span>
                      {formatPrice(land.price)}
                    </p>
                  )}
                  <button
                    onClick={() => onOpenDetail(land)}
                    className="w-full px-4 py-2 bg-primary hover:bg-primary-dark text-white text-sm font-medium rounded-md transition-colors"
                  >
                    {t('عرض التفاصيل', 'View Details')}
                  </button>
                </div>
              </Popup>
            </Marker>
          ))
          )}

          {/* Compass Indicator and Action Buttons */}
          <div className="absolute bottom-5 right-5 z-[1000] flex flex-col-reverse items-end gap-3">
            {/* Action Buttons - Vertical Stack */}
            <div className="flex flex-col gap-3">
              {/* Reload Button */}
              <button
                onClick={fetchLands}
                title={t('إعادة تحميل الخريطة', 'Reload Map')}
                className={`${
                  isMobile ? 'w-12 h-12' : 'w-14 h-14'
                } rounded-full bg-primary hover:bg-primary-dark text-white shadow-lg hover:shadow-xl transition-all flex items-center justify-center`}
              >
                <RefreshIcon className={isMobile ? 'w-5 h-5' : 'w-6 h-6'} />
              </button>

              {/* WhatsApp Buttons from Component */}
              {WhatsAppButtons && <WhatsAppButtons />}
            </div>

            {/* Compass */}
            <div className="relative bg-white/30 rounded-full w-24 h-24 flex items-center justify-center shadow-lg backdrop-blur-sm scale-[0.7] translate-x-[22px] translate-y-[10px]">
              <div className="relative w-full h-full flex items-center justify-center">
                <span className="absolute top-2 left-1/2 -translate-x-1/2 text-xs font-bold text-gray-800">N</span>
                <span className="absolute bottom-2 left-1/2 -translate-x-1/2 text-xs font-bold text-gray-800">S</span>
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-800">E</span>
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-800">W</span>
                <div className="w-1 h-1 rounded-full bg-primary" />
                <div className="absolute top-1/2 left-1/2 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-b-[20px] border-b-primary origin-bottom -translate-x-1/2 -translate-y-[60%]" />
              </div>
            </div>
          </div>
        </LeafletMap>
      )}
    </div>
  );
};

export default MapView;
